<template>
  <div
    ref="IZ-select"
    :tabindex="disableSearch ? 0 : -1"
    :class="{ 'IZ-select': true, 'IZ-select--with-value': inputValue }"
    @keydown.up="onSelectByArrow"
    @keydown.down="onSelectByArrow"
    @keydown.enter="onEnter"
    @keydown.tab.esc="setBlured"
  >
    <div class="IZ-select__input-wrap">
      <slot name="input-before" />

      <div class="IZ-select__text" :style="{ color: colorText, 'white-space': 'break-spaces' }">{{ title }}</div>

      <div
        ref="IZ-select__input"
        :class="{
          'IZ-select__input': true,
          'IZ-select__input--focused': focused,
          'IZ-select__input--has-menu': hasMenu,
          'IZ-select__input--has-error': disabled ? '' : hasError,
          'IZ-select__input--successful': successful,
          'IZ-select__input--selection-slot': showSelectionSlot,
          'IZ-select__input--disabled': disabled,
          'IZ-select__input--readonly': readonly
        }"
        @click="onClick"
      >
        <slot name="input-start" />

        <slot v-if="disabled ? '' : showSelectionSlot" :item="selectedItem" name="selection" />

        <input
          ref="IZ-select__input-for-text"
          v-bind="{
            value: inputValue,
            placeholder,
            class: inputForTextClass,
            disabled: disableSearch || disabled,
            readonly,
            tabindex: disableSearch ? -1 : 0,
            type: 'text',
            role: 'combobox',
            autocomplete: 'new-password',
            ...inputElCustomAttributes,
            style: inputForTextStyles
          }"
          @keyup="onSearchKeyUp"
          @keydown="onSearchKeyDown"
          @input="onSearch"
          @mousedown="onClick"
          @focus="setFocused(true)"
          @keypress="onlyTest"
          spellcheck="false"
          :minlength="min"
          :maxlength="max"
        />

        <button tabindex="-1">
          <icon class="IZ-select__icon" :name="currentIcon" :size="16" @click="arrowDown()" />
        </button>
        <slot name="input-end" />
      </div>

      <slot name="input-after" />
    </div>

    <transition name="fade">
      <div
        v-if="hasMenu"
        ref="IZ-select__menu"
        :style="menuDynamicStyles"
        :class="{ 'IZ-select__menu': true, 'IZ-select__menu--disable-search': disableSearch }"
      >
        <slot name="before-items-fixed" />

        <div
          ref="IZ-select__menu-items"
          :style="{ 'max-height': menuItemsMaxHeight }"
          class="IZ-select__menu-items"
          @scroll="onScroll"
        >
          <slot name="before-items">
            <div style="height: 2px;" />
          </slot>

          <div>
            <div
              v-for="(item, i) in itemsComputed"
              v-show="i < scrollItemsLimitCurrent || (arrowsIndex && i <= arrowsIndex)"
              ref="items"
              :key="'IZ-item-' + i"
              :class="{ 'IZ-select__item': true, 'IZ-select__item--selected': isItemSelected(item) }"
              @click="onClickSelectItem(item, i)"
            >
              <slot :item="item" name="item">
                <span> {{ getItemText(item) }} </span>
              </slot>
            </div>
          </div>

          <div v-if="nenhumDado()" class="IZ-select__no-data">
            <slot name="no-data">
              Nenhum dado disponível
            </slot>
          </div>

          <slot name="after-items">
            <div style="height: 2px;" />
          </slot>
        </div>

        <slot name="after-items-fixed" />

        <div style="position: absolute; top: 0; left: 0; right: 0;">
          <slot name="before-items-fixed-absolute" />
        </div>
        <div style="position: absolute; bottom: 0; left: 0; right: 0;">
          <slot name="after-items-fixed-absolute" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { isObject, getOffsetSum } from './helpers.js'
import eventsListeners from './eventsListeners'
import props from './props'
import computed from './computed'
export default {
  name: 'AutoCompleteCadu',
  props,
  data() {
    return {
      wishShowMenu: false,
      arrowsIndex: null,
      focused: false,
      selectedItem: null,
      selectedItemByArrows: null,
      searchData: '',
      scrollItemsLimitCurrent: this.scrollItemsLimit,
      mousedownListener: null,
      mousedownListenerArrow: null,
      index: -1
    }
  },
  computed,
  watch: {
    searchText(val) {
      this.setSearchData(val)
    },
    value() {
      this.setSelectedItemByValue()
    },
    items() {
      this.setSelectedItemByValue()
    },
    selectedItem() {
      this.selectedItemByArrows = null
      if (this.currentItemValue) {
        this.items.find((e, index) => {
          if (e === this.currentItemValue) {
            this.index = index
          }
        })
      } else {
        this.index = -1
      }

      this.$emit('index', this.index)
      this.$emit('input', this.currentItemValue)
    },
    itemsComputed(items) {
      this.$emit('change-displayed-items', items)
    }
  },
  created() {
    if (this.eventEmitter) {
      this.eventEmitter.on('set-search', this.setSearchData)
    }
    this.setSelectedItemByValue()
  },
  mounted() {
    this.mousedownListener = window.addEventListener('mousedown', ({ target }) => {
      const select = this.$refs['IZ-select']
      if (this.focused && select && !select.contains(target)) {
        this.setBlured()
      }
    })
  },
  beforeDestroy() {
    window.removeEventListener('mousedown', this.mousedownListener)
    window.removeEventListener('mousedown', this.mousedownListenerArrow)
  },
  methods: {
    ...eventsListeners,
    onlyTest(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode

      if (this.onlyNumber) {
        evt = evt || window.event
        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
          evt.preventDefault()
        } else {
          return true
        }
      } else if (this.onlyText) {
        const char = String.fromCharCode(charCode)
        if (!/[a-zA-ZzáàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ+. {2}]/.test(char)) {
          event.preventDefault()
        }
      } else if (this.onlyAlphaNum) {
        let re = /[^A-Z0-9 ]/gi
        if (re.test(String.fromCharCode(charCode))) {
          event.preventDefault()
        }
      }
    },
    getSearchData() {
      return this.searchData
    },
    setSearchData(val) {
      this.searchData = val
      this.$emit('update:search-text', val)
    },
    setInputFocused() {
      this.$refs['IZ-select__input-for-text'].focus()
    },
    setFocused(byInput = false) {
      if (this.focused || this.disabled || this.readonly) return

      if (!this.disableSearch && !byInput) {
        this.setInputFocused()
      }

      if (window.scrollTo && this.allowMobileScroll && this.isMobile) {
        const { top } = getOffsetSum(this.$refs['IZ-select__input'])
        window.scrollTo({
          top: top - 8,
          behavior: 'smooth'
        })
      }
      this.focused = true
      this.showMenu()
      this.$emit('focus')
    },
    arrowDown() {
      this.mousedownListenerArrow = window.addEventListener('mousedown', ({ target }) => {
        if (this.focused) {
          this.setBlured()
        }
      })
    },
    setBlured() {
      if (this.resetSearchOnBlur) {
        this.setSearchData('')
      }
      this.focused = false
      this.hideMenu()
      this.$refs['IZ-select__input-for-text'].blur()
      this.$emit('blur')
    },
    fireSelectEvent(item) {
      this.selectedItemByArrows = null
      this.$nextTick(() => {
        this.$emit('select', item)
      })
    },
    getItemText(item) {
      if (!item) return null
      if (this.itemText) return item[this.itemText]
      if (isObject(item)) {
        const keys = Object.keys(item)
        if (keys.length === 1) {
          return item[keys[0]]
        }
        return item
      }
      return item
    },
    getItemValue(item) {
      if (item == null) return null
      if (this.itemValue) return item[this.itemValue]
      if (isObject(item)) {
        const keys = Object.keys(item)
        if (keys.length === 1) {
          return item[keys[0]]
        }
        return item
      }
      return item
    },
    setSelectedItemByValue() {
      if (!this.items.length) {
        this.selectedItem = null
        return
      }
      const values = this.items.find((i, index) => {
        if (isObject(this.value)) {
          const valFromObjVal = this.getItemValue(this.value)
          return this.getItemValue(i) === valFromObjVal
        }
        return this.getItemValue(i) === this.value
      })
      if (values) {
        this.selectedItem = values
      } else {
        this.selectedItem = null
      }
    },
    filteredBySearchItems(items) {
      if (!this.getSearchData() || this.disableFilteringBySearch) return items
      return items.filter((i) => this.filter(i, this.getSearchData(), this.getItemText(i)))
    },
    isItemSelected(item) {
      return item === this.selectedItemByArrows || (item === this.selectedItem && !this.selectedItemByArrows)
    },
    showMenu() {
      if (this.hasMenu) return
      this.wishShowMenu = true
    },
    hideMenu() {
      if (!this.hasMenu) return
      this.wishShowMenu = false
    },
    nenhumDado() {
      return !this.itemsComputed.length && !this.loading
    }
  }
}
</script>

<style lang="sass">
@import "./Style/main.sass"
button
  outline: none !important
</style>
